import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import FloatingBookButton from "../components/FloatingBookButton";
import IllustrationReviewBlock from "../components/IllustrationReviewBlock";
import { LockedBlur } from "../components/LockedBlur";
import RolloverWizard from "../components/rollover/RolloverWizard";
import { progressEvent } from "../lib/progress";
import { api, userAuth, appUserAuth } from "../lib/api";
import { ArrowRight, Calculator as CalcIcon, EnvelopeSimple, Trophy, ShieldCheck, BookmarkSimple, ClockCounterClockwise, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n) => "$" + Math.round(n || 0).toLocaleString();

const TABS = [
  {
    id: "rollover",
    label: "Rollover",
    title: "401(k) / IRA Rollover",
    hookA: "What could your 401(k)",
    hookB: <>do for you <span className="gold-text italic">after you retire?</span></>,
    blurb: "Model a rollover from a 401(k), IRA, Roth, 403(b) or 457 into an IUL or Fixed Indexed Annuity. Every assumption is editable, you get year-by-year values, and you can compare it side by side with keeping the money where it is.",
  },
  {
    id: "iul",
    label: "IUL",
    title: "Indexed Universal Life",
    hookA: "What if your retirement income",
    hookB: <>never paid <span className="gold-text italic">tax?</span></>,
    blurb: "Drag the dials. Watch what a properly designed IUL turns into over time. Tax-free retirement income via policy loans plus a death benefit your family inherits tax-free.",
  },
  {
    id: "annuity",
    label: "FIA",
    title: "Fixed-Indexed Annuity",
    hookA: "What if a paycheck",
    hookB: <>showed up <span className="gold-text italic">every month — for life?</span></>,
    blurb: "Drag the dials. See what an FIA with a lifetime income rider turns into — a contractual paycheck the carrier owes you for life, even if your account runs to zero.",
  },
  {
    id: "whole_life",
    label: "Whole Life",
    title: "Whole Life Insurance",
    hookA: "What if your wealth",
    hookB: <>guaranteed itself — <span className="gold-text italic">forever?</span></>,
    blurb: "Drag the dials. Whole Life is the slowest builder and the most certain. Guaranteed cash value, dividends, and a death benefit your heirs inherit tax-free.",
  },
  {
    id: "term",
    label: "Term",
    title: "Level Term Life",
    hookA: "What if the cheapest coverage",
    hookB: <>bought <span className="gold-text italic">the biggest peace of mind?</span></>,
    blurb: "Term life is pure protection at the lowest cost. Great for young families, mortgage coverage, or a death benefit bridge while you build permanent policies.",
  },
  {
    id: "final_expense",
    label: "Final Expense",
    title: "Final Expense (Burial) Insurance",
    hookA: "What if goodbye",
    hookB: <>never became <span className="gold-text italic">a bill for your family?</span></>,
    blurb: "Small-face whole life for ages 50-85. Simple health questions or guaranteed issue. Coverage from $5,000 to $50,000 to handle funeral and final bills without paperwork.",
  },
];

const HEALTH_OPTS = [
  { v: "preferred_plus", label: "Preferred Plus" },
  { v: "preferred", label: "Preferred" },
  { v: "standard_plus", label: "Standard Plus" },
  { v: "standard", label: "Standard" },
  { v: "substandard", label: "Substandard" },
];

const GOAL_OPTS = [
  { v: "income", label: "Retirement income" },
  { v: "legacy", label: "Legacy / heirs" },
  { v: "liquidity", label: "Liquidity / banking" },
  { v: "accumulation", label: "Accumulation / growth" },
  { v: "protection", label: "Protection / LTC" },
];

const US_STATES = [
  ["", "— Select state —"],
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["DC","D.C."],["FL","Florida"],
  ["GA","Georgia"],["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],
  ["IA","Iowa"],["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],
  ["MD","Maryland"],["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],
  ["MS","Mississippi"],["MO","Missouri"],["MT","Montana"],["NE","Nebraska"],
  ["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],["NM","New Mexico"],
  ["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],
  ["SC","South Carolina"],["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],
  ["UT","Utah"],["VT","Vermont"],["VA","Virginia"],["WA","Washington"],
  ["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
];

const TOBACCO_OPTS = [
  { v: "non_tobacco",         label: "Non-tobacco" },
  { v: "former_smoker",       label: "Former smoker (>12 mo)" },
  { v: "occasional_cigar",    label: "Occasional cigar" },
  { v: "chewing_tobacco",     label: "Chewing tobacco" },
  { v: "vaping",              label: "Vaping / e-cig" },
  { v: "marijuana",           label: "Marijuana only" },
  { v: "nicotine_replacement",label: "Nicotine replacement (gum/patch)" },
  { v: "current_smoker",      label: "Current smoker" },
];

const OCCUPATION_OPTS = [
  { v: "office",              label: "Office / professional" },
  { v: "self_employed",       label: "Self-employed" },
  { v: "healthcare",          label: "Healthcare / medical" },
  { v: "teacher",             label: "Teacher / educator" },
  { v: "sales",               label: "Sales / real estate" },
  { v: "construction",        label: "Construction / trades" },
  { v: "police",              label: "Police / law enforcement" },
  { v: "firefighter",         label: "Firefighter" },
  { v: "pilot",               label: "Pilot / aviation" },
  { v: "commercial_diver",    label: "Commercial diver" },
  { v: "logging",             label: "Logging / forestry" },
  { v: "mining",              label: "Mining / drilling" },
  { v: "professional_athlete",label: "Professional athlete" },
  { v: "other",               label: "Other / not listed" },
];

const PURPOSE_OPTS = [
  { v: "protection",       label: "Family protection" },
  { v: "income",           label: "Retirement income" },
  { v: "legacy",           label: "Legacy / inheritance" },
  { v: "business",         label: "Business (key-person, buy-sell)" },
  { v: "estate_planning",  label: "Estate planning" },
];

const CONDITION_LABELS = [
  { key: "diabetes",       label: "Diabetes" },
  { key: "heart_disease",  label: "Heart disease" },
  { key: "copd",           label: "COPD / lung disease" },
  { key: "sleep_apnea",    label: "Sleep apnea" },
  { key: "stroke",         label: "Stroke / TIA" },
  { key: "cancer",         label: "Cancer" },
  { key: "depression",     label: "Depression" },
  { key: "anxiety",        label: "Anxiety" },
];

const MED_LABELS = [
  { key: "blood_pressure", label: "Blood pressure" },
  { key: "insulin",        label: "Insulin" },
  { key: "metformin",      label: "Metformin" },
  { key: "statin",         label: "Statin (cholesterol)" },
  { key: "pain",           label: "Pain (non-opioid)" },
  { key: "opioid",         label: "Opioid painkiller" },
  { key: "depression",     label: "Antidepressant" },
  { key: "anxiety",        label: "Anti-anxiety" },
  { key: "adhd",           label: "ADHD med" },
  { key: "sleep",          label: "Sleep aid" },
  { key: "blood_thinner",  label: "Blood thinner" },
  { key: "heart",          label: "Heart medication" },
  { key: "cancer",         label: "Cancer treatment" },
];

const SEVERITY_LABELS = ["None", "Mild", "Controlled", "Moderate", "On meds", "Insulin/Advanced", "Complications"];

export default function Calculator() {
  const [searchParams] = useSearchParams();
  const _resolveInitialTab = () => {
    // ?product= (preferred, from Quiz) OR ?focus= (legacy) take precedence
    const q = (searchParams.get("product") || searchParams.get("focus") || "").toLowerCase();
    if (q === "rollover") return "rollover";
    const VALID = ["iul", "annuity", "fia", "wholelife", "whole", "whole_life", "term", "final_expense", "finalexpense"];
    if (q && VALID.includes(q)) {
      if (q === "annuity" || q === "fia") return "annuity";
      if (q === "wholelife" || q === "whole" || q === "whole_life") return "whole_life";
      if (q === "term") return "term";
      if (q === "final_expense" || q === "finalexpense") return "final_expense";
      if (q === "iul") return "iul";
    }
    try {
      const raw = localStorage.getItem("ex_user_recommendation");
      if (raw) {
        const rec = JSON.parse(raw);
        const map = { iul: "iul", annuity: "annuity", whole_life: "whole_life" };
        return map[rec.primary] || "iul";
      }
    } catch (_e) { /* ignore parse errors */ }
    return "iul";
  };
  const [tab, setTab] = useState(_resolveInitialTab());
  const [age, setAge] = useState(35);
  const [monthly, setMonthly] = useState(500);
  const [retireAge, setRetireAge] = useState(65);
  const [sex, setSex] = useState("male");
  const [health, setHealth] = useState("standard");
  const [goal, setGoal] = useState("income");
  const [usState, setUsState] = useState("");
  // Digital underwriter (advanced) state
  const [uwOpen, setUwOpen] = useState(false);
  const [uw, setUw] = useState({
    height_in: 70,
    weight_lb: 180,
    tobacco: "non_tobacco",
    diabetes: 0, heart_disease: 0, copd: 0, sleep_apnea: 0,
    stroke: 0, cancer: 0, depression: 0, anxiety: 0,
    meds: [],
    family_cvd_early: false, family_cancer_early: false,
    duis: 0, reckless_driving: 0, license_suspensions: 0,
    speeding_tickets: 0, at_fault_accidents: 0,
    occupation: "office",
    annual_income: 0, net_worth: 0,
    existing_coverage: 0, face_amount_target: 0,
    purpose: "protection",
  });
  const uwEngaged = uwOpen; // If the panel is open, always send underwriter data
  const [result, setResult] = useState(null);
  const [carriers, setCarriers] = useState(null);
  const [showCapture, setShowCapture] = useState(false);
  const [identity, setIdentity] = useState({ name: "", email: "" });
  const [captured, setCaptured] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [saveForm, setSaveForm] = useState({ email: appUserAuth.email() || userAuth.email() || "", label: "" });
  const [saving, setSaving] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [showPdf, setShowPdf] = useState(false);
  const [pdfForm, setPdfForm] = useState({
    name: "",
    email: appUserAuth.email() || userAuth.email() || "",
  });
  const [pdfLoading, setPdfLoading] = useState(false);

  const tabSpec = TABS.find((t) => t.id === tab) || TABS[0];

  // Load previously saved scenarios for the current user
  useEffect(() => { progressEvent("calc_start"); }, []);

  useEffect(() => {
    const em = appUserAuth.email() || userAuth.email();
    if (!em) return;
    (async () => {
      try {
        const r = await api.get(`/calculator/scenarios?email=${encodeURIComponent(em)}`);
        setSavedScenarios(r.data?.scenarios || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const loadScenario = (s) => {
    setTab(s.product === "wholelife" ? "whole_life" : s.product);
    setAge(s.age);
    setMonthly(s.monthly_premium);
    setRetireAge(s.target_retirement_age);
    setSex(s.sex);
    setHealth(s.health_class);
    setGoal(s.goal);
    toast.success(`Loaded: ${s.label}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveScenario = async () => {
    if (!saveForm.email) { toast.error("Enter an email to save"); return; }
    setSaving(true);
    try {
      const r = await api.post("/calculator/scenarios", {
        product: tab, age, sex, health_class: health,
        monthly_premium: monthly, target_retirement_age: retireAge, goal,
        email: saveForm.email,
        label: saveForm.label || undefined,
      });
      toast.success("Scenario saved.");
      progressEvent("calc_complete", { via: "save" });
      setSavedScenarios((prev) => [r.data.scenario, ...prev]);
      setShowSave(false);
      setSaveForm({ email: saveForm.email, label: "" });
    } catch {
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const exportPdf = async () => {
    setPdfLoading(true);
    try {
      const payload = {
        name: pdfForm.name || "",
        email: pdfForm.email || "",
        product: tab,
        ranker: {
          product: tab,
          age,
          sex,
          health_class: health,
          monthly_premium: monthly,
          target_retirement_age: retireAge,
          goal,
          state: usState,
          ...(uwEngaged ? { underwriter: uw } : {}),
        },
      };
      const res = await api.post("/calculator/export-pdf", payload, { responseType: "blob" });
      // Trigger browser download
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `exodus-${tab}-illustration.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Your PDF is downloading.");
      progressEvent("calc_complete", { via: "pdf" });
      setShowPdf(false);
    } catch {
      toast.error("Could not generate PDF. Try again.");
    } finally {
      setPdfLoading(false);
    }
  };

  const run = async (withEmail = false) => {
    if (tab === "rollover") return;
    try {
      const basePayload = {
        product: tab,
        age,
        monthly_premium: monthly,
        target_retirement_age: retireAge,
      };
      if (withEmail) { basePayload.name = identity.name; basePayload.email = identity.email; }

      const [projRes, rankRes] = await Promise.all([
        api.post("/calculator/tax-free-retirement", basePayload),
        api.post("/calculator/carrier-comparison", {
          ...basePayload,
          sex,
          health_class: health,
          goal,
          state: usState,
          ...(uwEngaged ? { underwriter: uw } : {}),
        }),
      ]);
      setResult(projRes.data);
      setCarriers(rankRes.data);
      if (withEmail) { progressEvent("calc_complete", { via: "email" }); setCaptured(true); setShowCapture(false); toast.success("Your projection is in your inbox."); }
    } catch {
      toast.error("Couldn't calculate. Try again.");
    }
  };

  useEffect(() => { run(false); /* eslint-disable-next-line */ }, [tab, age, monthly, retireAge, sex, health, goal, usState, uwEngaged, uw]);

  return (
    <div data-testid="calculator-page" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-36 pb-12">
        <p className="eyebrow"><span className="gold-divider" />Maximize Performance — {tabSpec.title}</p>
        <h1 data-testid="calc-headline" className="font-display text-5xl lg:text-7xl mt-4 leading-[0.95]">
          {tabSpec.hookA} {tabSpec.hookB}
        </h1>
        <p className="text-lg text-exodus-muted mt-8 max-w-2xl leading-relaxed">{tabSpec.blurb}</p>
      </section>

      {/* Saved scenarios quick-load strip */}
      {tab !== "rollover" && savedScenarios.length > 0 && (
        <section data-testid="calc-saved-strip" className="max-w-7xl mx-auto px-6 lg:px-12 pb-4">
          <div className="border border-exodus-gold-deep/40 bg-exodus-surface/40 backdrop-blur-sm px-5 py-4">
            <div className="flex items-center gap-2 mb-3">
              <ClockCounterClockwise size={16} className="text-exodus-gold" />
              <p className="eyebrow text-exodus-muted">Your saved scenarios</p>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
              {savedScenarios.slice(0, 8).map((s, i) => (
                <button
                  key={i}
                  data-testid={`calc-saved-scenario-${i}`}
                  onClick={() => loadScenario(s)}
                  className="shrink-0 text-left border border-exodus-border bg-exodus-bg hover:border-exodus-gold hover:bg-exodus-elevated transition-colors px-4 py-3 min-w-[220px]"
                >
                  <p className="text-sm gold-text truncate">{s.label}</p>
                  <p className="text-[11px] text-exodus-muted mt-1">
                    {new Date(s.created_at).toLocaleDateString()} · Top: {s.top_carriers?.[0]?.name || "—"}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tabs */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-6">
        <div className="flex flex-wrap items-stretch border border-exodus-border">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`calc-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex-1 min-w-[140px] px-6 py-4 text-left transition-colors ${
                tab === t.id
                  ? "bg-exodus-gold text-exodus-bg"
                  : "bg-exodus-surface text-exodus-text hover:bg-exodus-elevated"
              }`}
            >
              <p className={`text-xs eyebrow ${tab === t.id ? "text-exodus-bg" : "text-exodus-muted"}`}>{t.label}</p>
              <p className="font-display text-lg mt-1">{t.title}</p>
            </button>
          ))}
        </div>
      </section>

      {tab === "rollover" ? <RolloverWizard /> : (<>
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-16">
        <div className="grid lg:grid-cols-2 gap-px bg-exodus-border">
          {/* Inputs */}
          <div className="bg-exodus-surface p-8 lg:p-10 space-y-8">
            <div className="flex items-center gap-3">
              <CalcIcon size={20} className="text-exodus-gold" />
              <p className="eyebrow">Your inputs</p>
            </div>

            <Slider testid="calc-age" label="Your current age" value={age}
              min={25} max={65} step={1} onChange={setAge} display={`${age} years old`} />
            <Slider testid="calc-monthly" label="Monthly contribution" value={monthly}
              min={100} max={3000} step={50} onChange={setMonthly} display={fmt(monthly) + "/mo"} />
            <Slider testid="calc-retire-age" label={tab === "annuity" ? "Income start age" : "Target retirement age"} value={retireAge}
              min={Math.max(age + 5, 50)} max={80} step={1} onChange={setRetireAge} display={`Age ${retireAge}`} />

            {/* Sex */}
            <SegmentControl
              testid="calc-sex"
              label="Sex (for underwriting)"
              value={sex}
              onChange={setSex}
              options={[{ v: "male", label: "Male" }, { v: "female", label: "Female" }]}
            />

            {/* Health */}
            <SelectControl
              testid="calc-health"
              label="Health class"
              value={health}
              onChange={setHealth}
              options={HEALTH_OPTS}
            />

            {/* Goal */}
            <SelectControl
              testid="calc-goal"
              label="Primary goal"
              value={goal}
              onChange={setGoal}
              options={GOAL_OPTS}
            />

            {/* State — filters carriers to those licensed in the applicant's state */}
            <div>
              <label htmlFor="calc-state-select" className="block eyebrow text-xs text-exodus-muted mb-2">Client state (for carrier licensing)</label>
              <select
                id="calc-state-select"
                data-testid="calc-state"
                value={usState}
                onChange={(e) => setUsState(e.target.value)}
                className="w-full bg-exodus-elevated border border-exodus-border text-exodus-text px-4 py-3 focus:outline-none focus:border-exodus-gold"
              >
                {US_STATES.map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
              <p className="text-xs text-exodus-muted mt-2">Filters carriers to those broadly licensed in your state.</p>
            </div>

            {/* ─── Digital Underwriter (Advanced) ─── */}
            <div className="border border-exodus-border">
              <button
                type="button"
                data-testid="uw-toggle"
                onClick={() => setUwOpen(!uwOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-exodus-elevated hover:bg-exodus-bg transition-colors"
              >
                <span className="eyebrow text-xs gold-text">
                  Digital Underwriter {uwOpen ? "· ON" : "(optional advanced profile)"}
                </span>
                <span className="text-exodus-gold text-xl leading-none">{uwOpen ? "−" : "+"}</span>
              </button>

              {uwOpen && (
                <div data-testid="uw-panel" className="p-5 space-y-6 border-t border-exodus-border">
                  <p className="text-xs text-exodus-muted italic">
                    Estimates only. Final approval depends on the carrier's medical exam, APS, MIB, and Rx history.
                  </p>

                  {/* Body composition */}
                  <div className="grid grid-cols-2 gap-4">
                    <NumberField label="Height (in)" testid="uw-height"
                      value={uw.height_in} onChange={(v) => setUw({...uw, height_in: v})} min={48} max={84} />
                    <NumberField label="Weight (lb)" testid="uw-weight"
                      value={uw.weight_lb} onChange={(v) => setUw({...uw, weight_lb: v})} min={80} max={500} />
                  </div>

                  {/* Tobacco */}
                  <SelectControl testid="uw-tobacco" label="Tobacco / nicotine use"
                    value={uw.tobacco} onChange={(v) => setUw({...uw, tobacco: v})} options={TOBACCO_OPTS} />

                  {/* Conditions with severity 0-10 */}
                  <div>
                    <p className="eyebrow text-xs text-exodus-muted mb-3">Medical conditions (0 = none, 10 = severe/complications)</p>
                    <div className="space-y-3">
                      {CONDITION_LABELS.map(({key, label}) => (
                        <SeveritySlider key={key} testid={`uw-cond-${key}`} label={label}
                          value={uw[key]} onChange={(v) => setUw({...uw, [key]: v})} />
                      ))}
                    </div>
                  </div>

                  {/* Medications */}
                  <div>
                    <p className="eyebrow text-xs text-exodus-muted mb-3">Prescription medications currently taken</p>
                    <div className="grid grid-cols-2 gap-2">
                      {MED_LABELS.map(({key, label}) => {
                        const checked = uw.meds.includes(key);
                        return (
                          <label key={key} data-testid={`uw-med-${key}`}
                            className={`flex items-center gap-2 px-3 py-2 border cursor-pointer text-sm transition-colors ${
                              checked ? "border-exodus-gold text-exodus-gold" : "border-exodus-border text-exodus-muted hover:text-exodus-text"
                            }`}>
                            <input type="checkbox" className="hidden"
                              checked={checked}
                              onChange={() => setUw({
                                ...uw,
                                meds: checked ? uw.meds.filter(m => m !== key) : [...uw.meds, key],
                              })} />
                            <span>{checked ? "✓" : "○"}</span> {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Family history */}
                  <div>
                    <p className="eyebrow text-xs text-exodus-muted mb-3">Family history (parent or sibling before age 60)</p>
                    <div className="flex gap-3">
                      <ChipToggle testid="uw-fam-cvd" label="Early CVD / heart" active={uw.family_cvd_early}
                        onClick={() => setUw({...uw, family_cvd_early: !uw.family_cvd_early})} />
                      <ChipToggle testid="uw-fam-cancer" label="Early cancer" active={uw.family_cancer_early}
                        onClick={() => setUw({...uw, family_cancer_early: !uw.family_cancer_early})} />
                    </div>
                  </div>

                  {/* Driving history */}
                  <div>
                    <p className="eyebrow text-xs text-exodus-muted mb-3">Driving history (past 3-5 years)</p>
                    <div className="grid grid-cols-2 gap-3">
                      <NumberField testid="uw-dui" label="DUIs" value={uw.duis} onChange={(v) => setUw({...uw, duis: v})} min={0} max={5} />
                      <NumberField testid="uw-reckless" label="Reckless" value={uw.reckless_driving} onChange={(v) => setUw({...uw, reckless_driving: v})} min={0} max={5} />
                      <NumberField testid="uw-suspension" label="Suspensions" value={uw.license_suspensions} onChange={(v) => setUw({...uw, license_suspensions: v})} min={0} max={5} />
                      <NumberField testid="uw-speeding" label="Speeding tix" value={uw.speeding_tickets} onChange={(v) => setUw({...uw, speeding_tickets: v})} min={0} max={10} />
                      <NumberField testid="uw-accidents" label="At-fault accidents" value={uw.at_fault_accidents} onChange={(v) => setUw({...uw, at_fault_accidents: v})} min={0} max={5} />
                    </div>
                  </div>

                  {/* Occupation */}
                  <SelectControl testid="uw-occupation" label="Occupation"
                    value={uw.occupation} onChange={(v) => setUw({...uw, occupation: v})} options={OCCUPATION_OPTS} />

                  {/* Financial UW */}
                  <div>
                    <p className="eyebrow text-xs text-exodus-muted mb-3">Financial underwriting (used for large face amounts)</p>
                    <div className="space-y-3">
                      <NumberField testid="uw-income" label="Annual income ($)" value={uw.annual_income} onChange={(v) => setUw({...uw, annual_income: v})} min={0} max={5000000} step={1000} />
                      <NumberField testid="uw-networth" label="Net worth ($)" value={uw.net_worth} onChange={(v) => setUw({...uw, net_worth: v})} min={0} max={100000000} step={5000} />
                      <NumberField testid="uw-existing" label="Existing life coverage ($)" value={uw.existing_coverage} onChange={(v) => setUw({...uw, existing_coverage: v})} min={0} max={20000000} step={10000} />
                      <NumberField testid="uw-target-face" label="Target face amount ($)" value={uw.face_amount_target} onChange={(v) => setUw({...uw, face_amount_target: v})} min={0} max={20000000} step={10000} />
                      <SelectControl testid="uw-purpose" label="Purpose of coverage"
                        value={uw.purpose} onChange={(v) => setUw({...uw, purpose: v})} options={PURPOSE_OPTS} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Output */}
          <div className="bg-exodus-bg p-8 lg:p-10">
            <p className="eyebrow text-exodus-muted">Projected outcome</p>

            {result && (
              <LockedBlur testid="calc-output-locked" label="Your projection is ready">
              <div data-testid="calc-output" className="space-y-8 mt-6">
                <div>
                  <p className="text-xs eyebrow text-exodus-muted">Years to grow</p>
                  <p className="font-display text-3xl mt-1">{result.years} years</p>
                </div>
                <div>
                  <p className="text-xs eyebrow text-exodus-muted">Total contributions</p>
                  <p className="font-display text-3xl mt-1">{fmt(result.total_contributions)}</p>
                </div>

                <div className="border-t border-exodus-border pt-8">
                  <p className="text-xs eyebrow gold-text">Projected cash value</p>
                  <p data-testid="calc-cash-value" className="font-display text-5xl mt-2 gold-text">{fmt(result.projected_cash_value)}</p>
                </div>

                {tab === "iul" && (
                  <>
                    <div>
                      <p className="text-xs eyebrow gold-text">Tax-free monthly income</p>
                      <p data-testid="calc-monthly-income" className="font-display text-5xl mt-2">{fmt(result.projected_monthly_tax_free_income)}<span className="text-base text-exodus-muted">/mo</span></p>
                      <p className="text-xs text-exodus-muted mt-2">{fmt(result.projected_annual_tax_free_income)}/year, tax-free, for life</p>
                    </div>
                    <div>
                      <p className="text-xs eyebrow text-exodus-muted">Death benefit estimate</p>
                      <p className="font-display text-2xl mt-1">{fmt(result.death_benefit_estimate)}</p>
                      <p className="text-xs text-exodus-muted mt-1">Income-tax-free to your heirs</p>
                    </div>
                  </>
                )}

                {tab === "annuity" && (
                  <>
                    <div>
                      <p className="text-xs eyebrow text-exodus-muted">Income account value</p>
                      <p className="font-display text-3xl mt-1">{fmt(result.projected_income_account)}</p>
                      <p className="text-xs text-exodus-muted mt-1">Figurative — used to calculate your lifetime check</p>
                    </div>
                    <div>
                      <p className="text-xs eyebrow gold-text">Guaranteed lifetime monthly check</p>
                      <p data-testid="calc-monthly-income" className="font-display text-5xl mt-2">{fmt(result.projected_monthly_lifetime_income)}<span className="text-base text-exodus-muted">/mo</span></p>
                      <p className="text-xs text-exodus-muted mt-2">{fmt(result.projected_annual_lifetime_income)}/year, contractually guaranteed for life</p>
                    </div>
                  </>
                )}

                {tab === "whole_life" && (
                  <>
                    <div>
                      <p className="text-xs eyebrow gold-text">Tax-free monthly income via loans</p>
                      <p data-testid="calc-monthly-income" className="font-display text-5xl mt-2">{fmt(result.projected_monthly_tax_free_income)}<span className="text-base text-exodus-muted">/mo</span></p>
                      <p className="text-xs text-exodus-muted mt-2">{fmt(result.projected_annual_tax_free_income)}/year — borrow against cash value, no 1099</p>
                    </div>
                    <div>
                      <p className="text-xs eyebrow text-exodus-muted">Projected death benefit</p>
                      <p className="font-display text-3xl mt-1">{fmt(result.projected_death_benefit)}</p>
                      <p className="text-xs text-exodus-muted mt-1">Permanent · tax-free to heirs · grows with dividends</p>
                    </div>
                  </>
                )}

                <p className="text-xs text-exodus-muted leading-relaxed border-t border-exodus-border pt-6">
                  {result.assumptions?.note}
                </p>

                {/* ══════════════════════════════════════════════════════
                    Premium vs Cost breakdown — transparency block
                    ══════════════════════════════════════════════════════ */}
                <PremiumCostBreakdown
                  product={tab}
                  monthlyPremium={monthly}
                  years={result.years || 1}
                  totalContributed={result.total_contributions || 0}
                  projectedCashValue={result.projected_cash_value || 0}
                />

                {/* ══════════════════════════════════════════════════════
                    Results-focused Book CTA — hero number + strategy session
                    ══════════════════════════════════════════════════════ */}
                <IllustrationReviewBlock
                  heroLabel={
                    tab === "annuity"
                      ? "Guaranteed lifetime monthly check"
                      : tab === "term"
                      ? "Death benefit protection"
                      : tab === "final_expense"
                      ? "Final expense coverage"
                      : "Tax-free monthly income"
                  }
                  heroValue={fmt(
                    tab === "annuity"
                      ? result.projected_monthly_lifetime_income
                      : (tab === "term" || tab === "final_expense")
                      ? result.projected_death_benefit
                      : result.projected_monthly_tax_free_income
                  )}
                  heroSuffix={(tab === "term" || tab === "final_expense") ? "" : "/mo"}
                />
              </div>
              </LockedBlur>
            )}

            <div className="mt-10 space-y-3">
              {!captured && (
                <button
                  data-gate-require
                  data-testid="calc-email-cta"
                  onClick={() => setShowCapture(true)}
                  className="w-full inline-flex items-center justify-center gap-3 text-sm py-4 border border-exodus-gold text-exodus-gold hover:bg-exodus-gold hover:text-exodus-bg transition-colors"
                >
                  <EnvelopeSimple size={16} /> Email me this projection
                </button>
              )}
              {!localStorage.getItem("ex_user_recommendation") && <Link
                to="/quiz"
                data-testid="calc-quiz-cta"
                className="w-full btn-gold flex items-center justify-center gap-3"
              >
                See if {tabSpec.title} is right for you <ArrowRight size={14} weight="bold" />
              </Link>}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          TOP 5 CARRIER MATCH — scenario-based ranking
          ═══════════════════════════════════════════════════════════════ */}
      {carriers?.top_carriers?.length > 0 && (
        <section data-testid="carrier-match" className="max-w-7xl mx-auto px-6 lg:px-12 pb-32">
          <div className="border-t border-exodus-gold-deep/40 pt-14">
            <p className="eyebrow flex items-center gap-2">
              <Trophy size={16} weight="fill" className="text-exodus-gold" />
              Top 8 carriers · matched to your scenario
            </p>
            <div className="flex items-start justify-between gap-6 flex-wrap mt-3">
              <h2 className="font-display text-4xl lg:text-5xl max-w-3xl leading-tight">
                Best fit for a <span className="gold-text italic">{HEALTH_OPTS.find(h=>h.v===health)?.label.toLowerCase()}</span> {sex}, age {age}, focused on <span className="gold-text italic">{GOAL_OPTS.find(g=>g.v===goal)?.label.toLowerCase()}</span>.
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  data-gate-require
                  data-testid="calc-export-pdf-btn"
                  onClick={() => setShowPdf(true)}
                  className="inline-flex items-center gap-2 text-sm bg-exodus-gold text-exodus-bg hover:bg-exodus-gold-deep transition-colors px-5 py-3 whitespace-nowrap"
                >
                  <FilePdf size={16} weight="fill" /> Export PDF
                </button>
                <button
                  data-gate-require
                  data-testid="calc-save-scenario-btn"
                  onClick={() => setShowSave(true)}
                  className="inline-flex items-center gap-2 text-sm border border-exodus-gold text-exodus-gold hover:bg-exodus-gold hover:text-exodus-bg transition-colors px-5 py-3 whitespace-nowrap"
                >
                  <BookmarkSimple size={16} weight="fill" /> Save this scenario
                </button>
              </div>
            </div>
            <p className="text-sm text-exodus-muted mt-4 max-w-2xl">
              Ranked by product fit, age-band suitability, and goal alignment. Projections use each carrier&apos;s historical illustration positioning against the industry mean.
            </p>

            <LockedBlur testid="carrier-match-locked" label="Your carrier matches are ready">
            {/* Digital Underwriter summary — only when engaged */}
            {carriers?.underwriter && (
              <div data-testid="uw-summary" className="mt-8 border border-exodus-gold-deep/40 bg-exodus-elevated p-6 grid md:grid-cols-4 gap-6">
                <div>
                  <p className="eyebrow text-xs text-exodus-muted">Predicted rating class</p>
                  <p className="font-display text-2xl gold-text mt-2">{carriers.underwriter.predicted_rating_class}</p>
                  <p className="text-[11px] text-exodus-muted mt-1 italic">Estimate — final decision rests with the carrier underwriter.</p>
                </div>
                <div>
                  <p className="eyebrow text-xs text-exodus-muted">Medical Fit</p>
                  <p className="font-display text-3xl mt-2">{carriers.underwriter.medical_fit}<span className="text-base text-exodus-muted">/100</span></p>
                </div>
                <div>
                  <p className="eyebrow text-xs text-exodus-muted">Financial Fit</p>
                  <p className="font-display text-3xl mt-2">{carriers.underwriter.financial_fit}<span className="text-base text-exodus-muted">/100</span></p>
                </div>
                <div>
                  <p className="eyebrow text-xs text-exodus-muted">Carriers evaluated</p>
                  <p className="font-display text-3xl mt-2">{carriers.carriers_evaluated}</p>
                  {carriers.carriers_filtered_out_by_state > 0 && (
                    <p className="text-[11px] text-exodus-muted mt-1">
                      {carriers.carriers_filtered_out_by_state} excluded (state {carriers.inputs?.state || "n/a"})
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-exodus-border">
              {carriers.top_carriers.map((c, i) => (
                <div
                  key={c.slug}
                  data-testid={`carrier-card-${c.slug}`}
                  className={`p-6 bg-exodus-surface hover:bg-exodus-elevated transition-colors relative ${i === 0 ? "ring-1 ring-exodus-gold" : ""}`}
                >
                  {i === 0 && (
                    <p className="absolute top-3 right-3 text-[10px] eyebrow gold-text">Top match</p>
                  )}
                  <div className="flex items-center gap-2 text-exodus-muted">
                    <span className="font-display text-2xl gold-text">#{i + 1}</span>
                    <span className="text-xs eyebrow">AM Best {c.am_best}</span>
                  </div>
                  <h3 className="font-display text-xl mt-3">{c.name}</h3>

                  {/* Underwriting badge — instant / simplified / guaranteed / full */}
                  {c.underwriting_label && (
                    <p
                      data-testid={`carrier-uw-${c.slug}`}
                      className={`inline-block text-[10px] eyebrow mt-2 px-2 py-1 border ${
                        c.underwriting_type === "guaranteed_issue"
                          ? "border-emerald-400 text-emerald-400"
                          : c.underwriting_type === "simplified_issue"
                          ? "border-sky-400 text-sky-400"
                          : c.underwriting_type === "accelerated"
                          ? "border-exodus-gold text-exodus-gold"
                          : c.underwriting_type === "financial_suitability"
                          ? "border-purple-400 text-purple-400"
                          : "border-exodus-border text-exodus-muted"
                      }`}
                    >
                      {c.underwriting_label}
                    </p>
                  )}

                  <p className="text-xs text-exodus-muted leading-relaxed mt-3">{c.positioning}</p>

                  <div className="mt-5 space-y-3 border-t border-exodus-border pt-4">
                    <div>
                      <p className="text-[10px] eyebrow text-exodus-muted">Illustrated cash value</p>
                      <p className="font-display text-lg gold-text">{fmt(c.projected_cash_value)}</p>
                    </div>
                    {c.projected_monthly_income > 0 && (
                      <div>
                        <p className="text-[10px] eyebrow text-exodus-muted">Monthly income</p>
                        <p className="font-display text-lg">{fmt(c.projected_monthly_income)}<span className="text-xs text-exodus-muted">/mo</span></p>
                      </div>
                    )}
                    <div>
                      <p className="text-[10px] eyebrow text-exodus-muted">Death benefit</p>
                      <p className="font-display text-lg">{fmt(c.projected_death_benefit)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] eyebrow text-exodus-muted">Fit score</p>
                      <p className="font-display text-sm">{c.score} / 100</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </LockedBlur>

            <div className="mt-8 flex items-start gap-3 text-xs text-exodus-muted leading-relaxed">
              <ShieldCheck size={16} className="text-exodus-gold shrink-0 mt-0.5" />
              <p>{carriers.disclosure}</p>
            </div>
          </div>
        </section>
      )}
      </>)}

      {showPdf && (
        <div data-testid="calc-pdf-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowPdf(false)}>
          <div className="bg-exodus-bg border border-exodus-gold max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <FilePdf size={18} weight="fill" className="text-exodus-gold" />
              <p className="eyebrow gold-text">Export presentation PDF</p>
            </div>
            <h3 className="font-display text-2xl mb-2">Download your illustration.</h3>
            <p className="text-sm text-exodus-muted mb-6">
              A branded PDF with your inputs, projected outcomes, and the top 8 carrier recommendations — ready to share or bring to your consultation.
            </p>
            <input
              data-testid="calc-pdf-name"
              className="input-exodus mb-3"
              placeholder="Your full name"
              value={pdfForm.name}
              onChange={(e) => setPdfForm({ ...pdfForm, name: e.target.value })}
            />
            <input
              data-testid="calc-pdf-email"
              type="email"
              autoComplete="email"
              className="input-exodus mb-6"
              placeholder="Your email (optional)"
              value={pdfForm.email}
              onChange={(e) => setPdfForm({ ...pdfForm, email: e.target.value })}
            />
            <button
              data-testid="calc-pdf-submit"
              disabled={pdfLoading}
              onClick={exportPdf}
              className="btn-gold w-full disabled:opacity-40"
            >
              {pdfLoading ? "Generating PDF..." : "Download my illustration"}
            </button>
            <button
              data-testid="calc-pdf-cancel"
              onClick={() => setShowPdf(false)}
              className="w-full mt-3 text-sm text-exodus-muted hover:text-exodus-text"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showSave && (
        <div data-testid="calc-save-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowSave(false)}>
          <div className="bg-exodus-bg border border-exodus-gold max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <BookmarkSimple size={18} weight="fill" className="text-exodus-gold" />
              <p className="eyebrow gold-text">Save this scenario</p>
            </div>
            <h3 className="font-display text-2xl mb-2">Lock in this comparison.</h3>
            <p className="text-sm text-exodus-muted mb-6">
              We&apos;ll snapshot your inputs and the ranked top 5 carriers so you can revisit later. Advisors: pull it up on the next call to show what changed.
            </p>
            <input
              data-testid="calc-save-email"
              type="email"
              autoComplete="email"
              className="input-exodus mb-3"
              placeholder="Your email"
              value={saveForm.email}
              onChange={(e) => setSaveForm({ ...saveForm, email: e.target.value })}
            />
            <input
              data-testid="calc-save-label"
              className="input-exodus mb-6"
              placeholder={`Nickname (optional) — e.g. "${tabSpec.label} baseline"`}
              value={saveForm.label}
              onChange={(e) => setSaveForm({ ...saveForm, label: e.target.value })}
            />
            <button
              data-testid="calc-save-submit"
              disabled={!saveForm.email || saving}
              onClick={saveScenario}
              className="btn-gold w-full disabled:opacity-40"
            >
              {saving ? "Saving…" : "Save scenario"}
            </button>
          </div>
        </div>
      )}

      {showCapture && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowCapture(false)}>
          <div className="bg-exodus-bg border border-exodus-gold max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <p className="eyebrow gold-text mb-4">Send me my projection</p>
            <h3 className="font-display text-2xl mb-6">Where should I send the numbers?</h3>
            <input data-testid="calc-modal-name" className="input-exodus mb-3" placeholder="Full name"
              value={identity.name} onChange={(e) => setIdentity({ ...identity, name: e.target.value })} />
            <input data-testid="calc-modal-email" type="email" className="input-exodus mb-6" placeholder="Email address"
              value={identity.email} onChange={(e) => setIdentity({ ...identity, email: e.target.value })} />
            <button data-testid="calc-modal-submit" disabled={!identity.email || !identity.name}
              onClick={() => run(true)} className="btn-gold w-full disabled:opacity-40">
              Email it to me
            </button>
            <p className="text-xs text-exodus-muted text-center mt-4">No drip-blast. No spam. You&apos;ll see it once.</p>
          </div>
        </div>
      )}

      <Footer />
      <FloatingBookButton testid="calc-floating-book" />
    </div>
  );
}

function Slider({ testid, label, value, min, max, step, onChange, display }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <p className="text-xs eyebrow text-exodus-muted">{label}</p>
        <p data-testid={`${testid}-display`} className="font-display text-2xl gold-text">{display}</p>
      </div>
      <input
        type="range"
        data-testid={testid}
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-exodus-gold"
      />
      <div className="flex justify-between text-xs text-exodus-muted mt-1">
        <span>{min}</span><span>{max}</span>
      </div>
    </div>
  );
}

function SegmentControl({ testid, label, value, onChange, options }) {
  return (
    <div>
      <p className="text-xs eyebrow text-exodus-muted mb-3">{label}</p>
      <div className="grid grid-cols-2 border border-exodus-border" data-testid={testid}>
        {options.map((o) => (
          <button
            key={o.v}
            type="button"
            data-testid={`${testid}-${o.v}`}
            onClick={() => onChange(o.v)}
            className={`px-4 py-3 text-sm transition-colors ${
              value === o.v ? "bg-exodus-gold text-exodus-bg" : "bg-exodus-bg text-exodus-text hover:bg-exodus-elevated"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SelectControl({ testid, label, value, onChange, options }) {
  return (
    <div>
      <p className="text-xs eyebrow text-exodus-muted mb-3">{label}</p>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-exodus-bg border border-exodus-border px-4 py-3 text-sm text-exodus-text focus:outline-none focus:border-exodus-gold transition-colors"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function NumberField({ testid, label, value, onChange, min = 0, max = 100, step = 1 }) {
  return (
    <label className="block">
      <span className="text-xs eyebrow text-exodus-muted block mb-2">{label}</span>
      <input
        type="number"
        data-testid={testid}
        value={value ?? 0}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === "" ? 0 : Number(raw));
        }}
        className="w-full bg-exodus-bg border border-exodus-border px-4 py-2 text-sm text-exodus-text focus:outline-none focus:border-exodus-gold transition-colors"
      />
    </label>
  );
}

function SeveritySlider({ testid, label, value, onChange }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-exodus-muted">{label}</span>
        <span className={value >= 6 ? "text-red-400" : value >= 3 ? "gold-text" : "text-exodus-muted"}>
          {value === 0 ? "None" : value <= 2 ? "Mild" : value <= 4 ? "Controlled" : value <= 6 ? "Moderate" : value <= 8 ? "Advanced" : "Complications"}
        </span>
      </div>
      <input
        type="range"
        data-testid={testid}
        min={0}
        max={10}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-exodus-gold"
      />
    </div>
  );
}

function ChipToggle({ testid, label, active, onClick }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2 text-sm border transition-colors ${
        active
          ? "border-exodus-gold text-exodus-gold bg-exodus-bg"
          : "border-exodus-border text-exodus-muted hover:text-exodus-text"
      }`}
    >
      {active ? "✓ " : ""}{label}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/*  PREMIUM vs COST BREAKDOWN — client-facing transparency block            */
/*  Shows the real economics of the policy: what portion of every dollar    */
/*  becomes cash value vs. covers cost-of-insurance and admin, plus         */
/*  actionable fee-avoidance tips per product line.                         */
/* ═══════════════════════════════════════════════════════════════════════ */
const PRODUCT_COST_MODEL = {
  iul: {
    label: "Indexed Universal Life",
    // Illustrative cost split for a properly-funded (near MEC) IUL over the projection horizon.
    // Cost of insurance drops as cash value grows; these are 30-yr averages.
    coi_pct: 0.14,      // cost of insurance
    admin_pct: 0.03,    // policy fees + admin
    tips: [
      { title: "Fund near the MEC limit", body: "The more cash value in the policy relative to death benefit, the LESS cost of insurance eats each month. Under-funding is the #1 cause of IUL disappointment." },
      { title: "Use policy loans, never withdrawals", body: "Wash-loans (net-zero interest spread) let you access cash tax-free without triggering surrender charges. Withdrawals hit surrender fees in years 1-10." },
      { title: "Choose multi-index crediting", body: "Split between an uncapped S&P 500 account, a high-cap S&P account, and a volatility-controlled index. Never sit in one crediting method — cap-rate compression can crater a single strategy." },
    ],
  },
  whole_life: {
    label: "Whole Life",
    // 60/40 base-to-PUA design. Base carries higher COI, PUA is nearly all cash value.
    coi_pct: 0.22,
    admin_pct: 0.04,
    tips: [
      { title: "Insist on a 55-65% base / 35-45% PUA design", body: "Agents who sell 90% base are prioritizing their commission over your cash value. The 60/40 sweet spot lets your cash value cross total-paid-in around year 6-8 instead of year 12-14." },
      { title: "Never let the policy become a MEC", body: "If your paid-in premium exceeds the 7-pay limit, every future distribution (including loans) becomes taxable + a 10% penalty under age 59½. Your advisor should monitor this every year." },
      { title: "Use non-direct-recognition carriers", body: "Guardian and MassMutual pay dividends even on borrowed cash value. Direct-recognition carriers reduce dividends on the loaned portion — worth 30-50 basis points a year over a 30-year loan cycle." },
    ],
  },
  annuity: {
    label: "Fixed-Indexed Annuity",
    coi_pct: 0.00,       // no COI on annuity
    admin_pct: 0.012,    // annual rider fee if income rider elected
    tips: [
      { title: "Stay inside the 10% free-withdrawal corridor", body: "Every FIA lets you pull up to 10% of the account value annually with zero surrender penalty. Never take out more in a single year during the surrender period." },
      { title: "Only elect the income rider if you'll use it", body: "The Guaranteed Lifetime Income rider costs ~1% of the income account per year. That's worth every basis point IF you turn income on. If you never activate income, the rider fee is pure drag — skip it." },
      { title: "Split across two carriers", body: "Half in an accumulation-focused FIA (e.g. Athene), half in an income-rider FIA (e.g. Allianz 222). Same net commission for your advisor, dramatically better safety through carrier diversification." },
    ],
  },
  term: {
    label: "Term Life",
    coi_pct: 0.85,      // pure protection product
    admin_pct: 0.05,
    tips: [
      { title: "Convert BEFORE the level period ends", body: "Every conversion-eligible carrier locks in your original age and health class for the new permanent policy. Wait until expiry and premiums jump 5-10x." },
      { title: "Add a waiver-of-premium rider", body: "Adds only 5-8% to the premium and pays the premium for you if you become disabled. Almost always worth it." },
      { title: "Return-of-premium only if young", body: "The ROP rider adds ~30% to premium. Worth it only if you're under 40 AND likely to outlive the term. Otherwise the extra premium is better invested elsewhere." },
    ],
  },
};

function PremiumCostBreakdown({ product, monthlyPremium, years, totalContributed, projectedCashValue }) {
  const model = PRODUCT_COST_MODEL[product];
  if (!model) return null;

  const totalIn = totalContributed || (monthlyPremium * 12 * years);
  const coiTotal = totalIn * model.coi_pct;
  const adminTotal = totalIn * model.admin_pct;
  const totalFees = coiTotal + adminTotal;
  const netToCashValue = Math.max(0, totalIn - totalFees);
  // The projection includes growth on top of net contributions
  const growthOnTop = Math.max(0, projectedCashValue - netToCashValue);
  const feePctOfPremium = totalIn > 0 ? (totalFees / totalIn) * 100 : 0;

  return (
    <div data-testid="premium-cost-breakdown" className="mt-8 pt-8 border-t border-exodus-gold-deep/40">
      <p className="eyebrow gold-text mb-3">Where every dollar goes</p>
      <p className="text-xs text-exodus-muted leading-relaxed mb-6">
        Transparency block — this is what the {model.label.toLowerCase()} actually charges vs. what compounds for you over {years} years.
      </p>

      {/* Segmented bar */}
      <div className="relative h-3 bg-exodus-bg rounded-full overflow-hidden border border-exodus-border mb-4">
        {product !== "annuity" && product !== "term" && (
          <div
            className="absolute top-0 left-0 h-full bg-exodus-gold"
            style={{ width: `${(netToCashValue / (totalIn + growthOnTop)) * 100}%` }}
            title="Net to cash value"
          />
        )}
        <div
          className="absolute top-0 h-full bg-red-500/70"
          style={{
            left: `${(netToCashValue / (totalIn + growthOnTop)) * 100}%`,
            width: `${(totalFees / (totalIn + growthOnTop)) * 100}%`,
          }}
          title="Policy cost + admin"
        />
        <div
          className="absolute top-0 right-0 h-full bg-emerald-500/60"
          style={{ width: `${(growthOnTop / (totalIn + growthOnTop)) * 100}%` }}
          title="Compounded growth"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <BreakdownStat testid="cost-total-in" color="gold"  label="You pay in" value={fmt(totalIn)} sub={`${years} years @ ${fmt(monthlyPremium)}/mo`} />
        <BreakdownStat testid="cost-fees"    color="red"   label="Total policy cost" value={fmt(totalFees)} sub={`${feePctOfPremium.toFixed(1)}% of premium`} />
        <BreakdownStat testid="cost-net-cv"  color="text"  label="Net to cash value" value={fmt(netToCashValue)} sub="before growth" />
        <BreakdownStat testid="cost-growth"  color="emerald" label="Compounded growth" value={fmt(growthOnTop)} sub="what the market builds" />
      </div>

      {/* Fee-avoidance tips */}
      <div className="border-t border-exodus-border pt-6">
        <p className="eyebrow text-exodus-muted mb-3">How to avoid the fees the best we can</p>
        <div className="space-y-3">
          {model.tips.map((t, i) => (
            <div key={i} data-testid={`fee-tip-${i}`} className="flex gap-4 border-l-2 border-exodus-gold pl-4 py-1">
              <span className="font-display text-sm gold-text shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <p className="text-sm font-medium">{t.title}</p>
                <p className="text-xs text-exodus-muted leading-relaxed mt-1">{t.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-exodus-muted mt-6 leading-relaxed">
        Cost estimates use industry-average cost-of-insurance and admin percentages for properly-structured policies with the recommended carrier. Actual policy costs vary by carrier, age, sex, health class, and product design.
      </p>
    </div>
  );
}

function BreakdownStat({ testid, color, label, value, sub }) {
  const colorCls = {
    gold: "gold-text",
    red: "text-red-400",
    emerald: "text-emerald-300",
    text: "text-exodus-text",
  }[color] || "text-exodus-text";
  return (
    <div data-testid={testid}>
      <p className="text-[10px] eyebrow text-exodus-muted">{label}</p>
      <p className={`font-display text-xl mt-1 ${colorCls}`}>{value}</p>
      <p className="text-[10px] text-exodus-muted mt-1">{sub}</p>
    </div>
  );
}
